// Folder icons: a built-in set drawn on a 12×12 pixel grid in the text colour, so they
// take on whichever theme is showing, an emoji, or a small PNG people upload. All are
// saved with the repository's settings, never in the repository itself.

export const ICON_GRID = 12;
/** Uploads are redrawn at this size, twice the grid, so they stay sharp on any screen. */
export const ICON_PIXELS = 32;
export const MAX_CUSTOM_ICONS = 24;
export const MAX_ICON_DATA_LENGTH = 8000;
export const MAX_ICON_UPLOAD_BYTES = 2 * 1024 * 1024;
export const CUSTOM_ICON_PREFIX = "custom:";
export const EMOJI_ICON_PREFIX = "emoji:";
// One emoji: a flag, a keycap, or a pictograph with its variation selector, skin tone,
// tag sequence, and any ZWJ-joined parts, as in 👩🏽‍💻 or 🏴󠁧󠁢󠁳󠁣󠁴󠁿.
const PICTOGRAPH = String.raw`\p{Extended_Pictographic}[\uFE0E\uFE0F]?\p{Emoji_Modifier}?`;
const EMOJI = new RegExp(
  String.raw`^(?:\p{Regional_Indicator}{2}|[#*0-9]\uFE0F?\u20E3|${PICTOGRAPH}(?:[\u{E0020}-\u{E007E}]+\u{E007F})?(?:\u200D${PICTOGRAPH})*)$`,
  "u",
);
export const isEmoji = (value: string): boolean => value.length <= 32 && EMOJI.test(value);
export const emojiIconRef = (emoji: string): string => `${EMOJI_ICON_PREFIX}${emoji}`;
/** The emoji a reference names, or null for any other icon. */
export const emojiOf = (ref: string | null | undefined): string | null =>
  ref?.startsWith(EMOJI_ICON_PREFIX) ? ref.slice(EMOJI_ICON_PREFIX.length) : null;

interface BuiltinIcon {
  id: string;
  label: string;
  rows: readonly string[];
}

export const FOLDER_ICONS: readonly BuiltinIcon[] = [
  {
    id: "folder",
    label: "Folder",
    rows: [
      "............",
      ".####.......",
      ".#..#######.",
      ".#........#.",
      ".##########.",
      ".#........#.",
      ".#........#.",
      ".#........#.",
      ".#........#.",
      ".#........#.",
      ".##########.",
      "............",
    ],
  },
  {
    id: "star",
    label: "Star",
    rows: [
      ".....##.....",
      ".....##.....",
      "....####....",
      "############",
      ".##########.",
      "..########..",
      "...######...",
      "..########..",
      "..###..###..",
      ".###....###.",
      ".##......##.",
      "............",
    ],
  },
  {
    id: "heart",
    label: "Heart",
    rows: [
      "............",
      "............",
      ".###....###.",
      "#####..#####",
      "############",
      "############",
      ".##########.",
      "..########..",
      "...######...",
      "....####....",
      ".....##.....",
      "............",
    ],
  },
  {
    id: "book",
    label: "Book",
    rows: [
      "............",
      ".#########..",
      ".##.......#.",
      ".##.#####.#.",
      ".##.......#.",
      ".##.#####.#.",
      ".##.......#.",
      ".##.......#.",
      ".##.......#.",
      ".##########.",
      ".#........#.",
      ".##########.",
    ],
  },
  {
    id: "briefcase",
    label: "Work",
    rows: [
      "............",
      "....####....",
      "....#..#....",
      "############",
      "#..........#",
      "#..........#",
      "#####..#####",
      "#....##....#",
      "#..........#",
      "#..........#",
      "############",
      "............",
    ],
  },
  {
    id: "house",
    label: "Home",
    rows: [
      ".....##.....",
      "....####....",
      "...##..##...",
      "..##....##..",
      ".##......##.",
      "##........##",
      ".#........#.",
      ".#..###...#.",
      ".#..#.#...#.",
      ".#..#.#...#.",
      ".####.#####.",
      "............",
    ],
  },
  {
    id: "terminal",
    label: "Code",
    rows: [
      "............",
      "############",
      "#..........#",
      "#.#........#",
      "#..#.......#",
      "#...#......#",
      "#..#.......#",
      "#.#..####..#",
      "#..........#",
      "############",
      "............",
      "............",
    ],
  },
  {
    id: "bulb",
    label: "Idea",
    rows: [
      "............",
      "....####....",
      "...#....#...",
      "..#......#..",
      "..#......#..",
      "..#......#..",
      "...#....#...",
      "....#..#....",
      "....####....",
      "....####....",
      ".....##.....",
      "............",
    ],
  },
  {
    id: "flag",
    label: "Flag",
    rows: [
      "............",
      ".#########..",
      ".##########.",
      ".##########.",
      ".##########.",
      ".#########..",
      ".#..........",
      ".#..........",
      ".#..........",
      ".#..........",
      ".#..........",
      "............",
    ],
  },
  {
    id: "check",
    label: "Done",
    rows: [
      "............",
      "............",
      "...........#",
      "..........##",
      ".........##.",
      ".#......##..",
      ".##....##...",
      "..##..##....",
      "...####.....",
      "....##......",
      "............",
      "............",
    ],
  },
  {
    id: "calendar",
    label: "Calendar",
    rows: [
      "............",
      "..#......#..",
      "############",
      "############",
      "#..........#",
      "#.##.##.##.#",
      "#..........#",
      "#.##.##.##.#",
      "#..........#",
      "#..........#",
      "############",
      "............",
    ],
  },
  {
    id: "clock",
    label: "Clock",
    rows: [
      "............",
      "...######...",
      "..#......#..",
      ".#...#....#.",
      ".#...#....#.",
      ".#...#....#.",
      ".#...####.#.",
      ".#........#.",
      ".#........#.",
      "..#......#..",
      "...######...",
      "............",
    ],
  },
  {
    id: "mail",
    label: "Mail",
    rows: [
      "............",
      "............",
      "############",
      "##........##",
      "#.#......#.#",
      "#..#....#..#",
      "#...#..#...#",
      "#....##....#",
      "#..........#",
      "############",
      "............",
      "............",
    ],
  },
  {
    id: "chat",
    label: "Chat",
    rows: [
      "............",
      ".##########.",
      "#..........#",
      "#.########.#",
      "#..........#",
      "#.#####....#",
      "#..........#",
      ".###..#####.",
      "...#.#......",
      "...##.......",
      "...#........",
      "............",
    ],
  },
  {
    id: "music",
    label: "Music",
    rows: [
      "............",
      "....########",
      "....########",
      "....#......#",
      "....#......#",
      "....#......#",
      "....#......#",
      "..###....###",
      ".####...####",
      ".####...####",
      "..##.....##.",
      "............",
    ],
  },
  {
    id: "camera",
    label: "Photos",
    rows: [
      "............",
      "............",
      "....####....",
      ".##########.",
      "#....##....#",
      "#...#..#...#",
      "#..#....#..#",
      "#..#....#..#",
      "#...#..#...#",
      "#....##....#",
      ".##########.",
      "............",
    ],
  },
  {
    id: "globe",
    label: "Globe",
    rows: [
      "............",
      "...######...",
      "..#..##..#..",
      ".#..#..#..#.",
      ".#..#..#..#.",
      ".##########.",
      ".#..#..#..#.",
      ".#..#..#..#.",
      "..#..##..#..",
      "...######...",
      "............",
      "............",
    ],
  },
  {
    id: "flask",
    label: "Lab",
    rows: [
      "............",
      "...######...",
      "....#..#....",
      "....#..#....",
      "....#..#....",
      "...#....#...",
      "..#......#..",
      ".#..####..#.",
      "#..######..#",
      "#.########.#",
      ".##########.",
      "............",
    ],
  },
  {
    id: "gear",
    label: "Settings",
    rows: [
      "............",
      ".....##.....",
      "..##.##.##..",
      "..########..",
      "...##..##...",
      ".###....###.",
      ".###....###.",
      "...##..##...",
      "..########..",
      "..##.##.##..",
      ".....##.....",
      "............",
    ],
  },
  {
    id: "person",
    label: "People",
    rows: [
      "............",
      "....####....",
      "...######...",
      "...######...",
      "...######...",
      "....####....",
      "............",
      "..########..",
      ".##########.",
      ".##########.",
      ".##########.",
      "............",
    ],
  },
  {
    id: "coin",
    label: "Money",
    rows: [
      "............",
      "...######...",
      "..#......#..",
      ".#...#....#.",
      ".#..####..#.",
      ".#..#.....#.",
      ".#...##...#.",
      ".#.....#..#.",
      ".#..####..#.",
      "..#..#...#..",
      "...######...",
      "............",
    ],
  },
  {
    id: "leaf",
    label: "Nature",
    rows: [
      "............",
      ".........###",
      "......######",
      "....#######.",
      "...#######..",
      "..#######...",
      "..######....",
      "..#####.....",
      ".#.###......",
      "#...........",
      "............",
      "............",
    ],
  },
  {
    id: "cup",
    label: "Coffee",
    rows: [
      "............",
      "...#..#.....",
      "....#..#....",
      "...#..#.....",
      "............",
      "#########...",
      "#.......####",
      "#.......#..#",
      "#.......####",
      ".#.....#....",
      "..#####.....",
      "............",
    ],
  },
  {
    id: "bolt",
    label: "Energy",
    rows: [
      "............",
      "......####..",
      ".....####...",
      "....####....",
      "...####.....",
      "..########..",
      ".....####...",
      "....####....",
      "...####.....",
      "..###.......",
      "..#.........",
      "............",
    ],
  },
];

/** The pencil on a folder row, which opens Edit folder. It is not a folder icon. */
export const PENCIL_ROWS: readonly string[] = [
  "............",
  ".........##.",
  "........####",
  ".......####.",
  "......####..",
  ".....####...",
  "....####....",
  "...####.....",
  "..####......",
  "..###.......",
  ".##.........",
  "............",
];

const builtins = new Map(FOLDER_ICONS.map((icon) => [icon.id, icon]));

/** An SVG path that fills the grid's # cells, one rectangle per run along a row. */
export function pixelPath(rows: readonly string[]): string {
  let path = "";
  rows.forEach((row, y) => {
    for (const run of row.matchAll(/#+/g))
      path += `M${run.index} ${y}h${run[0].length}v1h-${run[0].length}z`;
  });
  return path;
}

export const isCustomIconId = (id: string): boolean => /^[a-z0-9]{8}$/.test(id);
export const customIconRef = (id: string): string => `${CUSTOM_ICON_PREFIX}${id}`;
/** The uploaded icon a reference names, or null for a built-in one. */
export const customIconId = (ref: string): string | null =>
  ref.startsWith(CUSTOM_ICON_PREFIX) ? ref.slice(CUSTOM_ICON_PREFIX.length) : null;
export const builtinIcon = (ref: string): BuiltinIcon | undefined => builtins.get(ref);

export function isIconRef(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const emoji = emojiOf(value);
  if (emoji !== null) return isEmoji(emoji);
  const id = customIconId(value);
  return id === null ? builtins.has(value) : isCustomIconId(id);
}

/** An uploaded icon as it is kept: a small PNG as a data URL. */
export function isIconData(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length <= MAX_ICON_DATA_LENGTH &&
    /^data:image\/png;base64,(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value)
  );
}

export function newCustomIconId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return [...bytes].map((byte) => "abcdefghijklmnopqrstuvwxyz0123456789"[byte % 36]).join("");
}

/** The label a picker or screen reader gives an icon. */
export function iconLabel(ref: string): string {
  const emoji = emojiOf(ref);
  return builtinIcon(ref)?.label ?? (emoji === null ? "Uploaded icon" : `Emoji ${emoji}`);
}

const SVG = "http://www.w3.org/2000/svg";
export function pixelIcon(rows: readonly string[], className: string): SVGSVGElement {
  const svg = document.createElementNS(SVG, "svg");
  svg.setAttribute("class", className);
  svg.setAttribute("viewBox", `0 0 ${ICON_GRID} ${ICON_GRID}`);
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const path = document.createElementNS(SVG, "path");
  path.setAttribute("d", pixelPath(rows));
  path.setAttribute("fill", "currentColor");
  svg.append(path);
  return svg;
}

/**
 * The element that shows an icon, or null when there is nothing to show: no icon, or
 * an uploaded one that has since been forgotten.
 */
export function iconElement(
  ref: string | undefined,
  uploads: Readonly<Record<string, string>>,
): Element | null {
  if (!ref) return null;
  const icon = builtinIcon(ref);
  if (icon) return pixelIcon(icon.rows, "folder-icon");
  const emoji = emojiOf(ref);
  if (emoji !== null) {
    const glyph = document.createElement("span");
    glyph.className = "folder-icon folder-emoji";
    glyph.setAttribute("aria-hidden", "true");
    glyph.textContent = emoji;
    return glyph;
  }
  const id = customIconId(ref);
  const data = id === null ? undefined : uploads[id];
  if (!data) return null;
  const image = document.createElement("img");
  image.className = "folder-icon";
  image.src = data;
  image.alt = "";
  image.draggable = false;
  return image;
}

const UPLOAD_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp", "image/svg+xml"];
export const ICON_UPLOAD_ACCEPT = UPLOAD_TYPES.join(",");

function readDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("The image could not be read."));
    reader.readAsDataURL(file);
  });
}

/**
 * Redraws an uploaded image as a small square PNG, fitted and centred. An SVG is drawn
 * as an image, which never runs its scripts, and only the pixels are kept.
 */
export async function rasterizeIcon(file: File): Promise<string> {
  if (!UPLOAD_TYPES.includes(file.type))
    throw new Error("Use a PNG, JPEG, GIF, WebP, or SVG image.");
  if (!file.size || file.size > MAX_ICON_UPLOAD_BYTES)
    throw new Error("Icons must be smaller than 2 MiB.");
  const image = new Image();
  // The page only loads images from itself or data URLs, never blob URLs.
  image.src = await readDataUrl(file);
  try {
    await image.decode();
  } catch {
    throw new Error("That image could not be opened.");
  }
  const width = image.naturalWidth || ICON_PIXELS,
    height = image.naturalHeight || ICON_PIXELS;
  const scale = Math.min(ICON_PIXELS / width, ICON_PIXELS / height);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = ICON_PIXELS;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser can't resize images.");
  context.imageSmoothingQuality = "high";
  const drawnWidth = width * scale,
    drawnHeight = height * scale;
  context.drawImage(image, (ICON_PIXELS - drawnWidth) / 2, (ICON_PIXELS - drawnHeight) / 2, drawnWidth, drawnHeight);
  const data = canvas.toDataURL("image/png");
  if (!isIconData(data)) throw new Error("That image is too detailed for an icon. Try a simpler one.");
  return data;
}
